import React, { useState } from 'react';
import axios from '../../helpers/AxiosInstance';
import {
  Header,
  FlashMessage,
  ErrorRender,
  setToken,
  DluchReportingApi,
  ProgressBar
} from '../../constants/SharedComponents';
import DateFilter from '../../components/DateFilter';
import MetricsCards from '../../components/MetricsCards';

import '../../stylesheets/components/Pages.css';
import { Grid, Cell } from 'react-foundation';
import { useLocation } from 'react-router-dom';

import DoughnutChart from '../../components/DoughnutChart';
import PieChart from '../../components/PieChart';
import PolarAreaChart from '../../components/PolarAreaChart';
import ComboBarLineChart from '../../components/ComboBarLineChart';
import TopicsBarChart from '../../components/TopicsBarChart';
import useCurrentUser from '../../hooks/useCurrentUser';

import { CSVLink } from 'react-csv';

import { FaDownload } from 'react-icons/fa';

export default function Reports() {
  const { state } = useLocation();
  const currentUser = useCurrentUser();
  const token = setToken(currentUser);
  const headerParams = { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 };


  // State for date filtering
  const [selectedDateOption, setSelectedDateOption] = useState('allTime');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [flashMessageObject, setFlashMessageObject] = React.useState({});
  const [attemptFetch, setAttemptFetch] = useState(false);

  const [csvData, setCsvData] = useState([[]]);

  const handleDateRangeChange = (filterType, dateFilter) => {
    setSelectedDateOption(filterType);
    if (filterType === 'dateRange') {
      setDateRange(dateFilter);
      setData(null);
      setCsvData([[]]);
      setLoading(true);
      getData(dateFilter);
    } else {
      setDateRange({ startDate: '', endDate: '' });
      setData(null);
      setCsvData([[]]);
      setLoading(true);
      getData({ startDate: '', endDate: '' });
    }
    // When you have backend API, add your fetch call here
  };

  function retryFetch() {
    setLoading(true);
    setError(false);
    setData(null);
    setCsvData([[]]);
    getData(dateRange);
  }

  const getData = async (dateFilter) => {
    try {
      let ReportApi = new DluchReportingApi('ReportIndex');
      if (dateFilter.startDate !== '' && dateFilter.endDate !== '') {
        const response = await axios.get(ReportApi.ApiRequestUrl() + `?fromDate=${dateFilter.startDate}&toDate=${dateFilter.endDate}`, headerParams);
        let responseData = response.data.content.reports;
        setData(responseData);
        constructCsvData(responseData);
        setError(false);
      }
      else {
        const response = await axios.get(ReportApi.ApiRequestUrl(), headerParams);
        let responseData = response.data.content.reports;
        setData(responseData);
        constructCsvData(responseData);
        setError(false);
      }
    }
    catch (err) {
      setData(null);
      setError(err);
    }
    finally {
      setLoading(false);
    }
  }

  const constructCsvData = (responseData) => {
    setCsvData([["Report", "Value"],
    ["Signed Up Users", responseData.numberOfActiveUsers],
    ["Number of Conversations", responseData.overallNumberOfConversationsCreated],
    ["Average Number of Conversations Per Manager", responseData.averageNumberOfConversationsPerManager],
    ["Average Number of Conversations Per Pair", responseData.averageNumberOfConversationsPerPair],
    ["Percentage that Shared Information With Manager", responseData.frequencyOfInformationSharedWithManagerPct],
    ["Average Number of Topics Discussed Per Conversation", responseData.averageNumberOfTopicsPerConversation],
    ["Average Number of Controls Per Conversation Topic", responseData.averageDetailsPerConversationTopic.averageNumberOfControlsPerTopic],
    ["Average Number of Actions Per Conversation Topic", responseData.averageDetailsPerConversationTopic.averageNumberOfActionsPerTopic],
    ["Total Number of Actions Marked Complete", responseData.totalNumberOfActionsMarkedComplete],
    ["Stress Temperature Check Response",
      `Not At All: ${responseData.frequencyOfResponsesForTemperatureCheck.notStressful},\nMidly: ${responseData.frequencyOfResponsesForTemperatureCheck.mildlyStressful},\nModerately: ${responseData.frequencyOfResponsesForTemperatureCheck.moderatelyStressful},\nVery: ${responseData.frequencyOfResponsesForTemperatureCheck.veryStressful},\nExtremely: ${responseData.frequencyOfResponsesForTemperatureCheck.extremelyStressful}`
    ],
    ["Number of Conversations Breakdown",
      `Completed: ${responseData.conversationStatusValues.numberOfCompletedConversations},\nIn Progress: ${responseData.conversationStatusValues.numberOfInProgressConversations},\nPending: ${responseData.conversationStatusValues.numberOfPendingConversations}`
    ],
    ["Conversation Prep Breakdown",
      `Completed: ${responseData.conversationPrepValues.totalConversationPrepComplete},\nNot Completed: ${responseData.conversationPrepValues.totalConversationPrepIncomplete}`
    ],
    ["Type of Preparation Completed (%)",
      `Guided Route: ${responseData.conversationPrepPctValues.guidedRoutePct},\nSelf-Reflective: ${responseData.conversationPrepPctValues.selfGuidedRoutePct},\nNo Prep: ${responseData.conversationPrepPctValues.noPrepPct}`
    ],
    ["Guided Route Results by Domain vs Benchmark Data", ""],
    ["Guided Route Results",
      `Demands: ${responseData.sitResultsBenchmarkResponseValues.scores.demands},\nControl: ${responseData.sitResultsBenchmarkResponseValues.scores.control},\nManagement Support: ${responseData.sitResultsBenchmarkResponseValues.scores.managementSupport},\nPeer Support: ${responseData.sitResultsBenchmarkResponseValues.scores.peerSupport},\nRelationships: ${responseData.sitResultsBenchmarkResponseValues.scores.relationships},\nRole: ${responseData.sitResultsBenchmarkResponseValues.scores.role},\nChange: ${responseData.sitResultsBenchmarkResponseValues.scores.change}`
    ],
    ["25th Percentile",
      `Demands: ${responseData.sitResultsBenchmarkResponseValues.benchmarkMin.demands},\nControl: ${responseData.sitResultsBenchmarkResponseValues.benchmarkMin.control},\nManagement Support: ${responseData.sitResultsBenchmarkResponseValues.benchmarkMin.managementSupport},\nPeer Support: ${responseData.sitResultsBenchmarkResponseValues.benchmarkMin.peerSupport},\nRelationships: ${responseData.sitResultsBenchmarkResponseValues.benchmarkMin.relationships},\nRole: ${responseData.sitResultsBenchmarkResponseValues.benchmarkMin.role},\nChange: ${responseData.sitResultsBenchmarkResponseValues.benchmarkMin.change}`
    ],
    ["75th Percentile",
      `Demands: ${responseData.sitResultsBenchmarkResponseValues.benchmarkMax.demands},\nControl: ${responseData.sitResultsBenchmarkResponseValues.benchmarkMax.control},\nManagement Support: ${responseData.sitResultsBenchmarkResponseValues.benchmarkMax.managementSupport},\nPeer Support: ${responseData.sitResultsBenchmarkResponseValues.benchmarkMax.peerSupport},\nRelationships: ${responseData.sitResultsBenchmarkResponseValues.benchmarkMax.relationships},\nRole: ${responseData.sitResultsBenchmarkResponseValues.benchmarkMax.role},\nChange: ${responseData.sitResultsBenchmarkResponseValues.benchmarkMax.change}`
    ],
    ["Topics Identified By Guided Route Results",
      `Demands: ${responseData.frequencyOfTopicsGeneratedFromSITResults.demands},\nControl: ${responseData.frequencyOfTopicsGeneratedFromSITResults.control},\nManagement Support: ${responseData.frequencyOfTopicsGeneratedFromSITResults.managementSupport},\nPeer Support: ${responseData.frequencyOfTopicsGeneratedFromSITResults.peerSupport},\nRelationships: ${responseData.frequencyOfTopicsGeneratedFromSITResults.relationships},\nRole: ${responseData.frequencyOfTopicsGeneratedFromSITResults.role},\nChange: ${responseData.frequencyOfTopicsGeneratedFromSITResults.change}`
    ],
    ["Topics Taken to Conversation",
      `Demands: ${responseData.frequencyOfTopicsDecidedForConversationResults.demands},\nControl: ${responseData.frequencyOfTopicsDecidedForConversationResults.control},\nManagement Support: ${responseData.frequencyOfTopicsDecidedForConversationResults.managementSupport},\nPeer Support: ${responseData.frequencyOfTopicsDecidedForConversationResults.peerSupport},\nRelationships: ${responseData.frequencyOfTopicsDecidedForConversationResults.relationships},\nRole: ${responseData.frequencyOfTopicsDecidedForConversationResults.role},\nChange: ${responseData.frequencyOfTopicsDecidedForConversationResults.change}`
    ]
    ])
  }

  React.useEffect(() => {
    if (currentUser?.token && !attemptFetch) {
      setAttemptFetch(true);
      retryFetch();
    }

    if (state?.hasOwnProperty('flash')) {
      setFlashMessageObject(state.flash);
    }
  }, [currentUser, state, attemptFetch]);

  return (
    <>
      <Header />
      <FlashMessage object={flashMessageObject} columnWrap={true} />
      
      <div className="grid-container">
        {/* Add Metrics Cards Section */}
        <Grid className="grid-padding-x">
          <Cell small={12}>
            <div className="page-content" style={{ paddingTop: '20px', paddingBottom: '20px' }}>
              <h1 className="page-content__title mb-3">
                Reporting
              </h1>

              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <DateFilter
                  onDateRangeChange={handleDateRangeChange}
                  selectedOption={selectedDateOption}
                  dateRange={dateRange}
                />

                {!loading && data && (
                  <CSVLink
                    data={csvData}
                    filename='ForwardTogetherReportData.csv'
                    className='export-button active'
                  >
                    Export
                    <FaDownload size={16} />
                  </CSVLink>
                )}
              </div>
            </div>
          </Cell>
        </Grid>

        {loading && (
          <ProgressBar loading={loading} />
        )}
        {!loading && data && (
          <>
            <Grid className="grid-padding-x">
              <Cell small={12}>
                <div className="page-content" style={{ paddingBottom: '20px' }}>
                  <MetricsCards
                    metricsData={data}
                    selectedDateOption={selectedDateOption}
                    dateRange={dateRange}
                  />
                </div>
              </Cell>
            </Grid>        
            {/* Existing Charts Section */}
            <Grid className="charts-grid-padding-x">{ }
              <Cell small={12} medium={4} large={4}>
                <PolarAreaChart
                  title="Stress Temperature Check Response"
                  value={data !== null ? Math.max(...Object.values(data.frequencyOfResponsesForTemperatureCheck)) : 0}
                  subtitle=""
                  data={data !== null ? [
                    data.frequencyOfResponsesForTemperatureCheck.notStressful,
                    data.frequencyOfResponsesForTemperatureCheck.mildlyStressful,
                    data.frequencyOfResponsesForTemperatureCheck.moderatelyStressful,
                    data.frequencyOfResponsesForTemperatureCheck.veryStressful,
                    data.frequencyOfResponsesForTemperatureCheck.extremelyStressful
                  ] : [0, 0, 0, 0, 0]}
                  labels={['Not At All', 'Mildly', 'Moderately', 'Very', 'Extremely']}
                  legendLabels={['Not At All', 'Mildly', 'Moderately', 'Very', 'Extremely']}
                  legendColors={['#F765A3', '#A155B9', '#7AFFC7', '#82F0FF', '#63ABFD']}
                  infoTooltip="This chart shows the distribution of stress check responses."
                />
              </Cell>
              <Cell small={12} medium={4} large={4}>
                <PieChart
                  title="Number of Conversations"
                  value={data !== null ? data.overallNumberOfConversationsCreated : 0}
                  subtitle=""
                  data={data !== null ? [
                    data.conversationStatusValues.numberOfCompletedConversations,
                    data.conversationStatusValues.numberOfInProgressConversations,
                    data.conversationStatusValues.numberOfPendingConversations
                  ] : [0, 0, 0]}
                  legendLabels={['Completed', 'In Progress', 'Pending']}
                  legendColors={['#A155B9', '#F765A3', '#165BAA']}
                />
              </Cell>
              <Cell small={12} medium={4} large={4}>
                <PieChart
                  title="Conversation Prep"
                  value={data !== null ? `${data.conversationPrepValues.totalConversationPrepComplete}/${data.overallNumberOfConversationsCreated}` : '0/0'}
                  subtitle=""
                  data={data !== null ? [
                    data.conversationPrepValues.totalConversationPrepComplete,
                    data.conversationPrepValues.totalConversationPrepIncomplete
                  ] : [0, 0]}
                  legendLabels={['Completed', 'Not Completed']}
                  legendColors={['#A155B9', '#F765A3']}
                />
              </Cell>
            </Grid>
            <Grid className="charts-grid-two-column" style={{ marginTop: 15 }}>
              <Cell small={12} medium={4} large={4} style={{ minWidth: 0 }}>
                <DoughnutChart metricsData={data} />
              </Cell>
              <Cell small={12} medium={8} large={8} style={{ minWidth: 0 }}>
                <ComboBarLineChart
                  title="Guided Route Results by Domain vs Benchmark Data"
                  subtitle=""
                  labels={["Demands", "Control", "Management Support", "Peer Support", "Relationships", "Role", "Change"]}
                  barData={data !== null ? [
                    data.sitResultsBenchmarkResponseValues.scores.demands,
                    data.sitResultsBenchmarkResponseValues.scores.control,
                    data.sitResultsBenchmarkResponseValues.scores.managementSupport,
                    data.sitResultsBenchmarkResponseValues.scores.peerSupport,
                    data.sitResultsBenchmarkResponseValues.scores.relationships,
                    data.sitResultsBenchmarkResponseValues.scores.role,
                    data.sitResultsBenchmarkResponseValues.scores.change
                  ] : [0.0, 0, 0.0, 0.0, 0.0, 0.0, 0.0]}
                  barLabel="Score"
                  barColor="#165BAA"
                  lineData={data !== null ? [
                    [
                      data.sitResultsBenchmarkResponseValues.benchmarkMin.demands,
                      data.sitResultsBenchmarkResponseValues.benchmarkMin.control,
                      data.sitResultsBenchmarkResponseValues.benchmarkMin.managementSupport,
                      data.sitResultsBenchmarkResponseValues.benchmarkMin.peerSupport,
                      data.sitResultsBenchmarkResponseValues.benchmarkMin.relationships,
                      data.sitResultsBenchmarkResponseValues.benchmarkMin.role,
                      data.sitResultsBenchmarkResponseValues.benchmarkMin.change
                    ],
                    [
                      data.sitResultsBenchmarkResponseValues.benchmarkMax.demands,
                      data.sitResultsBenchmarkResponseValues.benchmarkMax.control,
                      data.sitResultsBenchmarkResponseValues.benchmarkMax.managementSupport,
                      data.sitResultsBenchmarkResponseValues.benchmarkMax.peerSupport,
                      data.sitResultsBenchmarkResponseValues.benchmarkMax.relationships,
                      data.sitResultsBenchmarkResponseValues.benchmarkMax.role,
                      data.sitResultsBenchmarkResponseValues.benchmarkMax.change
                    ]
                  ] : [[0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0], [0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0]]}
                  lineLabels={["25th Percentile", "75th Percentile"]}
                  lineColors={["#DC3545", "#198754"]}
                />
              </Cell>
            </Grid>
            <Grid className="charts-grid-padding-x align-top" style={{ marginTop: 15, gridTemplateColumns: '2fr 1fr', columnGap: 8 }}>
              <Cell small={12} medium={8} large={8} style={{ minWidth: 0, paddingRight: 4 }}>
                <TopicsBarChart
                  title="Topics Identified vs Topics Taken to Conversation"
                  subtitle=""
                  labels={["Demands", "Control", "Management Support", "Peer Support", "Relationships", "Role", "Change"]}
                  topicsData={data !== null ? [
                    data.frequencyOfTopicsGeneratedFromSITResults.demands,
                    data.frequencyOfTopicsGeneratedFromSITResults.control,
                    data.frequencyOfTopicsGeneratedFromSITResults.managementSupport,
                    data.frequencyOfTopicsGeneratedFromSITResults.peerSupport,
                    data.frequencyOfTopicsGeneratedFromSITResults.relationships,
                    data.frequencyOfTopicsGeneratedFromSITResults.role,
                    data.frequencyOfTopicsGeneratedFromSITResults.change
                  ] : [0, 0, 0, 0, 0, 0, 0]}
                  takenData={data !== null ? [
                    data.frequencyOfTopicsDecidedForConversationResults.demands,
                    data.frequencyOfTopicsDecidedForConversationResults.control,
                    data.frequencyOfTopicsDecidedForConversationResults.managementSupport,
                    data.frequencyOfTopicsDecidedForConversationResults.peerSupport,
                    data.frequencyOfTopicsDecidedForConversationResults.relationships,
                    data.frequencyOfTopicsDecidedForConversationResults.role,
                    data.frequencyOfTopicsDecidedForConversationResults.change
                  ] : [0, 0, 0, 0, 0, 0, 0]}
                  topicsLabel="Number of Topics Found"
                  takenLabel="Taken to Conversation"
                  topicsColor="#165BAA"
                  takenColor="#A155B9"
                />
              </Cell>
              <Cell small={12} medium={4} large={4} style={{ minWidth: 0, paddingLeft: 4 }}></Cell>
            </Grid>
          </>
        )}

      </div>
    </>
  );
}